/**
 * Network-aware image loading hook
 * Handles mobile operator proxy issues and slow networks with adaptive timeouts
 */

import { useState, useEffect } from 'react';

interface NetworkInfo {
  effectiveType?: '2g' | '3g' | '4g' | 'slow-2g';
  downlink?: number;
  rtt?: number;
  saveData?: boolean;
}

interface UseNetworkAwareImageOptions {
  url: string | null | undefined;
  enabled?: boolean;
  onLoad?: () => void;
  onError?: (error: Error) => void;
  logAnalytics?: (data: {
    url: string;
    error: string;
    networkType?: string;
    userAgent: string;
    timeout: number;
  }) => void;
}

interface UseNetworkAwareImageResult {
  loaded: boolean;
  error: boolean;
  loading: boolean;
}

/**
 * Get adaptive timeout based on network quality
 * Mobile operators with proxies often have longer latencies
 */
function getAdaptiveTimeout(): number {
  // Try to detect network quality
  const nav = navigator as Navigator & {
    connection?: NetworkInfo;
    mozConnection?: NetworkInfo;
    webkitConnection?: NetworkInfo;
  };

  const connection = nav.connection || nav.mozConnection || nav.webkitConnection;

  if (!connection) {
    // No Network Information API - use conservative timeout
    return 15000; // 15s default for unknown connections
  }

  const { effectiveType, saveData, rtt } = connection;

  // If user has data saver enabled, use longer timeout (they expect slower loads)
  if (saveData) {
    return 30000; // 30s for data saver mode
  }

  // Adjust timeout based on network type
  switch (effectiveType) {
    case 'slow-2g':
    case '2g':
      return 30000; // 30s for very slow connections
    case '3g':
      return 20000; // 20s for 3G
    case '4g':
    default:
      // For 4G and unknown, check RTT if available
      if (rtt && rtt > 300) {
        return 20000; // High latency 4G (mobile operator proxy likely)
      }
      return 10000; // 10s for good 4G/WiFi
  }
}

/**
 * Detect if user is on mobile network (helps identify operator-related issues)
 */
function detectMobileOperator(): string | null {
  const userAgent = navigator.userAgent.toLowerCase();

  // Common mobile operator patterns in User-Agent
  const operatorPatterns = [
    { pattern: /orange/i, name: 'Orange' },
    { pattern: /sfr/i, name: 'SFR' },
    { pattern: /bouygues/i, name: 'Bouygues' },
    { pattern: /free/i, name: 'Free' },
    { pattern: /vodafone/i, name: 'Vodafone' },
    { pattern: /t-mobile/i, name: 'T-Mobile' },
    { pattern: /verizon/i, name: 'Verizon' },
    { pattern: /att/i, name: 'AT&T' },
  ];

  for (const { pattern, name } of operatorPatterns) {
    if (pattern.test(userAgent)) {
      return name;
    }
  }

  return null;
}

export function useNetworkAwareImage({
  url,
  enabled = true,
  onLoad,
  onError,
  logAnalytics,
}: UseNetworkAwareImageOptions): UseNetworkAwareImageResult {
  const [loaded, setLoaded] = useState(false);
  const [error, setError] = useState(false);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!url || !enabled) {
      setLoaded(true); // No image to load, mark as ready
      return;
    }

    let mounted = true;

    const timeout = getAdaptiveTimeout();
    const img = new Image();

    setLoading(true);
    setError(false);

    // Set adaptive timeout based on network quality
    const timeoutId = setTimeout(() => {
      if (mounted) {
        const errorMessage = `Image loading timeout after ${timeout}ms`;
        console.warn(errorMessage, url);

        // Log analytics for operator-related issues
        if (logAnalytics) {
          const nav = navigator as Navigator & { connection?: NetworkInfo };
          const connection = nav.connection;

          logAnalytics({
            url,
            error: 'timeout',
            networkType: connection?.effectiveType,
            userAgent: navigator.userAgent,
            timeout,
          });
        }

        setError(true);
        setLoaded(true); // Allow render without image
        setLoading(false);

        if (onError) {
          onError(new Error(errorMessage));
        }
      }
    }, timeout);

    img.onload = () => {
      clearTimeout(timeoutId);
      if (mounted) {
        console.log('Image loaded successfully:', url);
        setLoaded(true);
        setError(false);
        setLoading(false);

        if (onLoad) {
          onLoad();
        }
      }
    };

    img.onerror = () => {
      clearTimeout(timeoutId);
      if (mounted) {
        const errorMessage = 'Failed to load image';
        console.error(errorMessage, url);

        // Log analytics for operator-related issues
        if (logAnalytics) {
          const nav = navigator as Navigator & { connection?: NetworkInfo };
          const connection = nav.connection;
          const operator = detectMobileOperator();

          logAnalytics({
            url,
            error: operator ? `load_error_${operator}` : 'load_error',
            networkType: connection?.effectiveType,
            userAgent: navigator.userAgent,
            timeout,
          });
        }

        setError(true);
        setLoaded(true); // Allow render without image
        setLoading(false);

        if (onError) {
          onError(new Error(errorMessage));
        }
      }
    };

    // Start loading
    img.src = url;

    return () => {
      mounted = false;
      clearTimeout(timeoutId);
    };
  // Note: Callbacks are intentionally excluded from dependencies to prevent infinite loops
  // when inline functions are passed. The callbacks will use their initial values.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [url, enabled]);

  return { loaded, error, loading };
}
